// ============================================================================
// REVIEWS PAGE
// Unified tabbed interface for all review/approval queues
// ============================================================================

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ClipboardCheck,
  Users,
  FileText,
  Cloud,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AvatarReviewQueue from './AvatarReviewQueue';
import TemplateReviewQueue from './TemplateReviewQueue';
import SandboxApprovalPage from './SandboxApprovalPage';

interface ReviewTab {
  key: string;
  label: string;
  icon: React.ElementType;
  roles: string[];
  component: React.ComponentType;
}

const tabs: ReviewTab[] = [
  { key: 'avatars', label: 'Avatars', icon: Users, roles: ['avatar_admin', 'system_admin'], component: AvatarReviewQueue },
  { key: 'templates', label: 'Templates', icon: FileText, roles: ['template_admin', 'system_admin'], component: TemplateReviewQueue },
  { key: 'sandbox', label: 'Sandbox', icon: Cloud, roles: ['system_admin'], component: SandboxApprovalPage },
];

export default function ReviewsPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const visibleTabs = tabs.filter(
    (tab) => user && tab.roles.includes(user.role)
  );

  const initialTab = searchParams.get('tab') || visibleTabs[0]?.key || 'avatars';
  const [activeTab, setActiveTab] = useState(initialTab);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setSearchParams({ tab: key });
  };

  if (visibleTabs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-lg font-bold text-slate-900">Access Denied</h2>
          <p className="text-sm text-slate-500 mt-1">You don't have permission to view any review queues.</p>
        </div>
      </div>
    );
  }

  const current = visibleTabs.find((t) => t.key === activeTab) || visibleTabs[0];
  const ActiveComponent = current.component;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white px-8 pt-6 pb-0">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-cyan-600 flex items-center justify-center">
            <ClipboardCheck size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Reviews</h1>
            <p className="text-sm text-slate-500">Review and approve submissions</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1">
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = current.key === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
                  isActive
                    ? 'border-primary text-primary bg-primary/5'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto">
        <ActiveComponent />
      </div>
    </div>
  );
}
